// Pure ASO-score utilities, shared between:
//   - /resources/aso-score-checker (AsoScoreCheckerPage.jsx)
//   - the global Exit Intent modal (ExitIntentModal.jsx)
//
// All functions are deterministic and run in the browser — no network needed.

export function parseStoreUrl(raw) {
  if (!raw || !raw.trim()) return { ok: false, error: "Paste a store URL to begin." };
  const url = raw.trim();
  let platform = null, slug = "", appId = "", country = "";
  try {
    const u = new URL(url.startsWith("http") ? url : `https://${url}`);
    const host = u.hostname.toLowerCase();
    if (host.includes("apps.apple.com") || host.includes("itunes.apple.com")) {
      platform = "ios";
      const parts = u.pathname.split("/").filter(Boolean);
      country = parts[0] || "";
      const appIdx = parts.indexOf("app");
      if (appIdx >= 0 && parts[appIdx + 1]) slug = parts[appIdx + 1];
      const idPart = parts.find((p) => /^id\d+/i.test(p));
      if (idPart) appId = idPart.replace(/^id/i, "");
    } else if (host.includes("play.google.com")) {
      platform = "android";
      const id = u.searchParams.get("id");
      if (id) {
        appId = id;
        slug = id.split(".").pop();
      }
      country = u.searchParams.get("gl") || "";
    } else {
      return { ok: false, error: "URL must be an Apple App Store or Google Play link." };
    }
  } catch {
    return { ok: false, error: "That doesn't look like a valid URL." };
  }
  if (!slug) return { ok: false, error: "Couldn't read the app slug — check the URL." };
  return { ok: true, platform, slug, appId, country, raw };
}

function scoreSlug(slug) {
  const len = slug.length;
  const words = slug.split(/[-_.]+/).filter(Boolean);
  const hasNumbers = /\d/.test(slug);
  const tooShort = len < 5;
  const tooLong = len > 30;
  const idealWords = words.length >= 2 && words.length <= 4;
  let score = 60;
  if (idealWords) score += 20;
  if (!tooShort && !tooLong) score += 10;
  if (!hasNumbers) score += 10;
  if (words.some((w) => w.length > 12)) score -= 15;
  score = Math.max(15, Math.min(100, score));
  let why;
  if (tooShort) why = "Slug too short — likely brand-only with no keywords.";
  else if (tooLong) why = "Slug too long — search engines truncate.";
  else if (hasNumbers) why = "Numbers in slug rarely help ASO.";
  else if (idealWords) why = "Brand + 1–3 keywords — strong slug structure.";
  else why = "Slug is OK but could use a clearer keyword + brand combo.";
  return { score, why };
}
function scoreBrand(slug) {
  const root = slug.split(/[-_.]+/)[0] || "";
  const isGeneric = /(app|free|best|top|new|the|my|live|online|mobile)/i.test(root);
  let score = 70;
  if (root.length >= 3 && !isGeneric) score += 20;
  if (root.length >= 6) score += 10;
  if (isGeneric) score -= 30;
  score = Math.max(20, Math.min(100, score));
  return {
    score,
    why: isGeneric
      ? "Brand root looks generic — your app name competes with thousands of others."
      : "Brand root is distinctive and trademark-friendly.",
  };
}
function scoreKeyword(slug) {
  const words = slug.split(/[-_.]+/).filter((w) => w.length > 2);
  const distinct = new Set(words);
  const len = words.length;
  let score = 50;
  if (len >= 2) score += 25;
  if (len >= 3) score += 10;
  if (distinct.size === words.length) score += 10;
  score = Math.max(20, Math.min(100, score));
  return {
    score,
    why: len >= 2
      ? `${len} keyword tokens detected — good keyword surface area.`
      : "Only 1 token in slug — limited keyword surface area.",
  };
}
function scorePlatform(platform, country) {
  let score = 75;
  if (platform === "ios" && country && country.length === 2) score += 15;
  if (platform === "ios") score += 5;
  if (platform === "android" && country) score += 10;
  return {
    score: Math.min(100, score),
    why: platform === "ios"
      ? `iOS · region${country ? ` "${country.toUpperCase()}"` : " not specified"} detected.`
      : `Google Play${country ? ` · region "${country}"` : ""} detected.`,
  };
}
function scoreCompletion(slug, appId, platform) {
  let score = 60;
  if (appId) score += 25;
  if (platform === "ios" && appId.length >= 9) score += 10;
  if (platform === "android" && appId.includes(".")) score += 10;
  return {
    score: Math.min(100, score),
    why: appId
      ? "Store metadata link is well-formed."
      : "Couldn't find the app ID — URL may be incomplete.",
  };
}

export function computeScore(parsed) {
  const slug = parsed.slug;
  const subs = {
    slug: scoreSlug(slug),
    brand: scoreBrand(slug),
    keyword: scoreKeyword(slug),
    platform: scorePlatform(parsed.platform, parsed.country),
    completion: scoreCompletion(slug, parsed.appId, parsed.platform),
  };
  const weights = { slug: 0.25, brand: 0.20, keyword: 0.30, platform: 0.10, completion: 0.15 };
  const total = Math.round(
    Object.entries(subs).reduce((sum, [k, v]) => sum + v.score * weights[k], 0)
  );
  return { ...parsed, subs, total };
}

export const SUB_LABELS = {
  slug: "Slug structure",
  brand: "Brand strength",
  keyword: "Keyword density",
  platform: "Platform fit",
  completion: "Listing completeness",
};

export function verdict(total) {
  if (total >= 80) return { label: "Strong", color: "#10b981" };
  if (total >= 60) return { label: "Above average", color: "#2196F3" };
  if (total >= 40) return { label: "Needs work", color: "#f59e0b" };
  return { label: "Weak", color: "#ef4444" };
}

// ─── iTunes Search API (free · CORS-enabled · no auth) ──────
// Returns up to `limit` iOS apps matching the term. Used by both the
// standalone ASO Score Checker and the Exit Intent modal.
export async function searchItunes(term, { country = "us", limit = 8 } = {}) {
  if (!term || term.trim().length < 2) return [];
  const u = new URL("https://itunes.apple.com/search");
  u.searchParams.set("term", term.trim());
  u.searchParams.set("entity", "software");
  u.searchParams.set("limit", String(limit));
  u.searchParams.set("country", country);
  const res = await fetch(u.toString());
  if (!res.ok) return [];
  const data = await res.json();
  return (data.results || []).map((r) => ({
    name: r.trackName,
    developer: r.sellerName || r.artistName,
    icon: r.artworkUrl100 || r.artworkUrl60,
    rating: r.averageUserRating,
    ratings: r.userRatingCount,
    url: r.trackViewUrl,
    bundleId: r.bundleId,
    primaryGenre: r.primaryGenreName,
    country,
  }));
}
